import { useEffect, useEffectEvent, type Dispatch } from 'react';
import type { FlightFilterNote as FilterTextNote } from '../results/filters/filter_chips_copy.js';
import type { FlightFilterCabin } from '../results/filters/flight_filter_panel.js';
import { useExplorerEnv } from './explorer_env.js';
import type { ExplorerEvent, ExplorerState } from './explorer_state.js';

/**
 * Cabine pedida por texto (R2-D4): o MESMO gesto do clique em "Executiva" — `cabin.onSearch`
 * refaz a busca (o host ouve a troca, os chips zeram no pouso). Igual à atual ⇒ só a nota. Sem
 * seam (explorer congelado, host sem cabine) ⇒ nota honesta. Consome o pedido no reducer antes
 * de agir: um re-render não pode disparar duas buscas.
 */
export function useCabinRequest(input: {
  state: ExplorerState;
  send: Dispatch<ExplorerEvent>;
  cabin: FlightFilterCabin | undefined;
  notify: ((note: FilterTextNote, values?: Record<string, string>) => void) | undefined;
}): void {
  const { copy } = useExplorerEnv();
  const request = input.state.cabinRequest;
  // Lido NO MOMENTO em que o pedido chega, não no do render que o criou:
  // `copy`/`cabin`/`notify` são recriados a cada render do host, e listá-los nas
  // deps disparava o efeito de novo a cada re-render — uma SEGUNDA busca pro
  // mesmo pedido. Só o PEDIDO arma o efeito.
  const act = useEffectEvent((cabinRequest: NonNullable<typeof request>) => {
    const { send, cabin, notify } = input;
    send({ t: 'cabin_request_consumed' });
    const label = copy.filter.cabin[cabinRequest];
    if (!cabin) {
      notify?.('cabinUnavailable');
      return;
    }
    if ((cabin.value ?? 'economy') === cabinRequest) {
      notify?.('cabinSame', { cabin: label });
      return;
    }
    notify?.('cabin', { cabin: label });
    cabin.onSearch(cabinRequest);
  });
  useEffect(() => {
    if (request) act(request);
  }, [request]);
}
